"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Calendar, ArrowRight } from 'lucide-react';
function BlogArchiveContent() {
  const {
    posts,
    loading,
    error,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages,
    setPage
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <div className="lg:col-span-3 space-y-8">
          <div className="w-full h-[400px] bg-muted/20 animate-pulse rounded-md"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="h-[300px] bg-muted/20 animate-pulse rounded-md"></div>)}
          </div>
        </div>
        <div className="hidden lg:block lg:col-span-1">
          <div className="h-[200px] bg-muted/20 animate-pulse rounded-md"></div>
        </div>
      </div>;
  }
  if (error) {
    return <div className="text-center py-12">
        <p className="text-destructive mb-4">Failed to load the latest expedition logs.</p>
        <Button onClick={() => window.location.reload()} variant="outline" className="border-chart-3 text-chart-3 hover:bg-chart-3 hover:text-white rounded-md">
          Try Again
        </Button>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="text-center py-12 text-muted-foreground">
        <p className="font-default">No expedition stories available at the moment. Check back soon!</p>
      </div>;
  }
  const featuredPost = posts[0];
  const gridPosts = posts.slice(1);
  const trendingTopics = ["Safari Guides", "Mountain Trekking", "Coastal Escapes", "Gear Reviews", "Hidden Gems", "Travel Safety", "Eco-Tourism", "Photography"];
  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    if (startPage > 1) {
      items.push(<PaginationItem key="1" data-index="first">
          <PaginationLink onClick={() => setPage(1)} className="cursor-pointer font-mono text-xs">1</PaginationLink>
        </PaginationItem>);
      if (startPage > 2) {
        items.push(<PaginationItem key="ellipsis-start" data-index="ellipsis-start">
            <PaginationEllipsis />
          </PaginationItem>);
      }
    }
    for (let i = startPage; i <= endPage; i++) {
      items.push(<PaginationItem key={i} data-index={i}>
          <PaginationLink isActive={currentPage === i} onClick={() => setPage(i)} className={`cursor-pointer font-mono text-xs ${currentPage === i ? "bg-chart-2 text-primary" : ""}`}>
            {i}
          </PaginationLink>
        </PaginationItem>);
    }
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<PaginationItem key="ellipsis-end" data-index="ellipsis-end">
            <PaginationEllipsis />
          </PaginationItem>);
      }
      items.push(<PaginationItem key={totalPages} data-index="last">
          <PaginationLink onClick={() => setPage(totalPages)} className="cursor-pointer font-mono text-xs">{totalPages}</PaginationLink>
        </PaginationItem>);
    }
    return items;
  };
  return <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
      {}
      <div className="lg:col-span-3 flex flex-col gap-8">
        
        {}
        {featuredPost && <article className="group relative overflow-hidden rounded-md border border-border bg-card shadow-md">
            <Link to={featuredPost.link || "#"} className="block absolute inset-0 z-10">
              <span className="sr-only">Read {featuredPost.title}</span>
            </Link>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              <div className="relative aspect-video md:aspect-auto h-full overflow-hidden bg-muted">
                {featuredPost.featuredImage ? <img src={featuredPost.featuredImage} alt={featuredPost.title} className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105" /> : <div className="w-full h-full flex items-center justify-center bg-secondary/20">
                    <span className="text-muted-foreground font-mono text-xs">NO_IMAGE_LOGGED</span>
                  </div>}
                {featuredPost.categories && featuredPost.categories.length > 0 && <div className="absolute top-4 left-4 z-20">
                    <Badge variant="default" className="bg-chart-2 text-primary font-default uppercase tracking-wider rounded-md">
                      {featuredPost.categories[0].name}
                    </Badge>
                  </div>}
              </div>
              <div className="p-6 md:p-8 flex flex-col justify-center">
                <div className="flex items-center gap-4 text-xs font-mono text-chart-3 mb-4">
                  {featuredPost.date && <div className="flex items-center gap-1">
                      <SafeHydrate><Calendar className="w-3 h-3" /></SafeHydrate>
                      <span>{new Date(featuredPost.date).toLocaleDateString()}</span>
                    </div>}
                </div>
                <h3 className="text-2xl md:text-3xl font-default font-bold text-foreground mb-4 line-clamp-3 group-hover:text-chart-3 transition-colors uppercase tracking-tight">
                  {featuredPost.title}
                </h3>
                {featuredPost.excerpt && <div className="text-muted-foreground font-default font-light line-clamp-3 mb-6 leading-relaxed" dangerouslySetInnerHTML={{
              __html: featuredPost.excerpt
            }} />}
                <div className="mt-auto pt-4">
                  <span className="inline-flex items-center text-sm font-default font-bold text-chart-3 group-hover:underline underline-offset-4">
                    EXPLORE STORY <ArrowRight className="ml-2 w-4 h-4" />
                  </span>
                </div>
              </div>
            </div>
          </article>}

        {}
        {gridPosts.length > 0 && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
            {gridPosts.map((post, i) => <Card key={post.id} data-index={i} className="group overflow-hidden bg-card border-border hover:border-chart-2/50 transition-colors h-full flex flex-col rounded-md shadow-md">
                <Link to={post.link || "#"} className="block relative aspect-[4/3] overflow-hidden bg-muted">
                  {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105" /> : <div className="w-full h-full flex items-center justify-center bg-secondary/20">
                      <span className="text-muted-foreground font-mono text-xs">NO_DATA</span>
                    </div>}
                  {post.categories && post.categories.length > 0 && <div className="absolute top-3 left-3 z-10">
                      <Badge variant="secondary" className="bg-background/90 backdrop-blur-md text-chart-3 font-default uppercase text-[10px] tracking-widest rounded-md">
                        {post.categories[0].name}
                      </Badge>
                    </div>}
                </Link>
                <CardContent className="p-5 flex flex-col flex-1">
                  <div className="flex items-center gap-3 text-[10px] font-mono text-chart-3 mb-3">
                    {post.date && <time dateTime={post.date}>{new Date(post.date).toLocaleDateString()}</time>}
                  </div>
                  <h4 className="text-lg font-default font-bold text-foreground mb-3 line-clamp-2 group-hover:text-chart-3 transition-colors uppercase tracking-tight">
                    <Link to={post.link || "#"}>{post.title}</Link>
                  </h4>
                  {post.excerpt && <div className="text-sm text-muted-foreground font-default font-light line-clamp-3 mb-4 flex-1 leading-relaxed" dangerouslySetInnerHTML={{
              __html: post.excerpt
            }} />}
                </CardContent>
              </Card>)}
          </div>}

        {}
        {totalPages > 1 && <div className="mt-12 border-t border-border pt-8">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious onClick={hasPrev ? prevPage : undefined} className={!hasPrev ? "pointer-events-none opacity-50" : "cursor-pointer font-mono text-xs"} />
                </PaginationItem>
                
                {renderPaginationItems()}

                <PaginationItem>
                  <PaginationNext onClick={hasNext ? nextPage : undefined} className={!hasNext ? "pointer-events-none opacity-50" : "cursor-pointer font-mono text-xs"} />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>}
      </div>

      {}
      <aside className="hidden lg:block lg:col-span-1">
        <div className="sticky top-24 space-y-8">
          <div className="bg-card border border-border rounded-md p-6 shadow-md">
            <h3 className="text-lg font-default font-bold text-foreground mb-4 flex items-center gap-2 uppercase tracking-tight">
              <span className="w-2 h-2 rounded-md bg-chart-2 animate-pulse"></span>
              Expedition Intel
            </h3>
            <div className="flex flex-wrap gap-2">
              {trendingTopics.map((topic, i) => <Badge key={i} data-index={i} variant="outline" className="cursor-pointer font-mono text-[10px] border-chart-3 text-chart-3 hover:bg-chart-2 hover:text-primary hover:border-chart-2 transition-colors py-1.5 px-3 rounded-md">
                  {topic}
                </Badge>)}
            </div>
          </div>

          <div className="bg-primary border border-border rounded-md p-6 text-center shadow-lg">
            <h4 className="font-default font-bold text-chart-2 mb-2 uppercase tracking-tight">Join the Expedition</h4>
            <p className="text-sm text-white/80 font-default font-light mb-4">Get the latest field reports and exclusive travel deals delivered to your inbox.</p>
            <Button className="w-full bg-chart-2 text-primary hover:bg-chart-2/90 font-default font-bold rounded-md">SUBSCRIBE NOW</Button>
          </div>
        </div>
      </aside>
    </div>;
}
export default function LatestFromTheField() {
  const wp_query_params = {
    post_type: "post",
    per_page: 7,
    status: "publish"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="224531" id="latest-from-the-field" className="bg-background text-foreground py-24 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12 md:mb-16">
          <h2 className="text-4xl md:text-6xl font-default font-bold text-foreground mb-4 uppercase tracking-tighter">
            Latest from the Field
          </h2>
          <p className="text-lg text-muted-foreground font-default font-light max-w-2xl leading-relaxed">
            Dispatch from the world's most remote corners. Discover the stories, gear, and guides shaping the future of modern exploration.
          </p>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 7,
        status: ["publish"]
      }}>
          <BlogArchiveContent />
        </PostsProvider>
      </div>
    </section>;
}